import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { UserService } from './users.service';

export type SectionAccess = 'dashboard' | 'manage';

/**
 * Permisos efectivos del usuario por seccion.
 *
 * El mapa lo calcula el backend y llega en `/user/me/` como
 * `{ "<url de la seccion>": ["dashboard", "manage"] }`. Aqui solo se cachea y
 * se consulta: esconder una pestaña es una comodidad para el usuario, no la
 * barrera de seguridad. Los endpoints que exigen `manage` lo vuelven a
 * comprobar en el servidor, de modo que ocultar mal una pestaña nunca abre un
 * acceso que el rol no tenga.
 *
 * Compatibilidad: si `me` no trae `permissions` (backend antiguo) se concede
 * todo, para que la plataforma siga funcionando igual que antes del cambio.
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private permissions$: Observable<Record<string, string[]>> | null = null;
  private cached: Record<string, string[]> | null = null;
  private hasPermissionsPayload = true;

  constructor(private userService: UserService) {}

  /** Normaliza una URL de seccion igual que el backend (sin query ni barra final). */
  private normalize(url: string | null | undefined): string {
    if (!url) {
      return '';
    }
    const normalized = String(url).split('?')[0].replace(/\/+$/, '').toLowerCase();
    return normalized || '/';
  }

  /** Carga (una sola vez) el mapa de permisos desde `/user/me/`. */
  load(): Observable<Record<string, string[]>> {
    if (!this.permissions$) {
      this.permissions$ = this.userService.me().pipe(
        map((me: any) => {
          this.hasPermissionsPayload = !!me?.permissions;
          return (me?.permissions ?? {}) as Record<string, string[]>;
        }),
        catchError(() => {
          // Si no se pudo leer el mapa se concede todo en la UI, en lugar de
          // dejar la pantalla en blanco por un fallo de red. No abre nada: los
          // endpoints que exigen `manage` lo comprueban en el servidor.
          this.hasPermissionsPayload = false;
          return of({} as Record<string, string[]>);
        }),
        tap((permissions) => (this.cached = permissions)),
        shareReplay(1)
      );
    }
    return this.permissions$;
  }

  /** Invalida la cache: usar tras cambiar roles para releer los permisos. */
  reset(): void {
    this.permissions$ = null;
    this.cached = null;
    this.hasPermissionsPayload = true;
  }

  /** Consulta sincrona; requiere que `load()` ya haya emitido. */
  can(sectionUrl: string, access: SectionAccess = 'dashboard'): boolean {
    if (this.cached === null || !this.hasPermissionsPayload) {
      return true;
    }
    return (this.cached[this.normalize(sectionUrl)] ?? []).includes(access);
  }

  /** Consulta asincrona: resuelve cuando el mapa esta disponible. */
  can$(sectionUrl: string, access: SectionAccess = 'dashboard'): Observable<boolean> {
    if (this.cached !== null) {
      return of(this.can(sectionUrl, access));
    }
    return this.load().pipe(map(() => this.can(sectionUrl, access)));
  }
}
